import React, { useState, useMemo, useRef } from "react";
import { I } from "../data";
import { useWorkspace } from "../WorkspaceContext";
import { useConfirm } from "../components/ConfirmDialog";

const TAGS = ["Application", "Essay/SOP", "Test (IELTS/GRE)", "Reference", "Scholarship", "Document"];

function cleanTaskText(s) {
  if (!s) return s;
  let t = String(s).trim();
  for (let i = 0; i < 3; i++) {
    const before = t;
    const m = t.match(/^\[(.+)\]$/s) || t.match(/^\((.+)\)$/s) || t.match(/^"(.+)"$/s) ||
      t.match(/^'(.+)'$/s) || t.match(/^`(.+)`$/s) || t.match(/^“(.+)”$/s) || t.match(/^‘(.+)’$/s);
    if (m) t = m[1].trim();
    if (t === before) break;
  }
  return t;
}

function effectiveTags(t) {
  if (Array.isArray(t.tags) && t.tags.length) return t.tags.filter((x) => TAGS.includes(x));
  const s = `${t.title || ""} ${t.src || ""}`.toLowerCase();
  if (t.scholarship_id || /scholarship|chevening|fulbright|aga khan|inlaks|funding|grant|bursary/.test(s)) return ["Scholarship"];
  if (/\bsop\b|statement of purpose|personal statement|\bessay/.test(s)) return ["Essay/SOP"];
  if (/ielts|toefl|\bgre\b|gmat|\bsat\b|duolingo|english (test|proficiency)|\btest\b|\bexam\b/.test(s)) return ["Test (IELTS/GRE)"];
  if (/recommendation|reference|\blor\b|recommender|referee/.test(s)) return ["Reference"];
  if (/transcript|passport|\bcv\b|resume|portfolio|certificate|upload|financial|bank statement|\bdocument/.test(s)) return ["Document"];
  if (/appl|submit|deadline|portal|\bform\b|register|enroll|common app/.test(s)) return ["Application"];
  return [];
}

function parseDue(due) {
  const empty = { date: null, label: "", isToday: false, isUpcoming: false, isOverdue: false };
  if (!due) return empty;
  const raw = String(due).trim();
  const lower = raw.toLowerCase();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (lower === "today") return { date: today, label: "Today", isToday: true, isOverdue: false };
  if (lower === "tomorrow") { const d = new Date(today); d.setDate(d.getDate() + 1); return { date: d, label: "Tomorrow", isUpcoming: true }; }
  let d = null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  else { const hasYear = /\b\d{4}\b/.test(raw); const tmp = new Date(hasYear ? raw : `${raw} ${today.getFullYear()}`); if (!isNaN(tmp.getTime())) d = tmp; }
  if (!d || isNaN(d.getTime())) return { date: null, label: raw.length <= 16 ? raw : "", isUpcoming: true, freeform: true };
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return { date: d, label, isToday: diff === 0, isOverdue: diff < 0, isUpcoming: diff > 0 };
}

function dueSortValue(t) {
  const p = parseDue(t.due);
  if (p.date) return p.date.getTime();
  if (p.freeform) return Number.MAX_SAFE_INTEGER - 1;
  return Number.MAX_SAFE_INTEGER;
}

function bucketOf(t) {
  const p = parseDue(t.due);
  if (p.isOverdue) return "overdue";
  if (!p.date && !p.freeform) return "someday";
  if (p.date) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((p.date - today) / 86400000);
    if (diff <= 7) return "week";
    if (diff <= 14) return "next";
  }
  return "later";
}

const GROUPS = [
  { key: "overdue", label: "Overdue", over: true },
  { key: "week", label: "This week" },
  { key: "next", label: "Next week" },
  { key: "later", label: "Later" },
  { key: "someday", label: "No date yet" },
];

export default function Tasks({ go }) {
  const { tasks, universities, addTask, updateTask, deleteTask } = useWorkspace();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");
  const [uniFilter, setUniFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [showDone, setShowDone] = useState(false);

  const uniName = (id) => universities.find((u) => u.id === id)?.name;

  const matches = (t) => {
    if (search && !`${t.title || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (uniFilter !== "all" && t.uni_id !== uniFilter && t.uni !== uniName(uniFilter)) return false;
    if (tagFilter !== "all" && !effectiveTags(t).includes(tagFilter)) return false;
    return true;
  };

  const open = useMemo(() => tasks.filter((t) => !t.done && matches(t)).sort((a, b) => dueSortValue(a) - dueSortValue(b)), [tasks, search, uniFilter, tagFilter]); // eslint-disable-line
  const completed = useMemo(() => tasks.filter((t) => t.done && matches(t)), [tasks, search, uniFilter, tagFilter]); // eslint-disable-line

  const grouped = useMemo(() => {
    const g = {}; GROUPS.forEach((x) => (g[x.key] = []));
    open.forEach((t) => g[bucketOf(t)].push(t));
    return g;
  }, [open]);

  const toggle = (t) => updateTask(t.id, { done: !t.done });
  const remove = async (t) => {
    const ok = await confirm({ title: "Delete task?", message: `"${cleanTaskText(t.title)}" will be removed.`, confirmLabel: "Delete", danger: true });
    if (ok) await deleteTask(t.id);
  };

  const actionFor = (t) => {
    const s = `${t.title || ""} ${t.src || ""}`.toLowerCase();
    if (effectiveTags(t).includes("Scholarship")) return { label: "Open", fn: () => go && go("scholarships") };
    if (t.ai_suggested || /email|reply|outreach|recommend|professor|reach out/.test(s)) {
      const u = universities.find((x) => x.id === t.uni_id || x.name === t.uni);
      return { label: "Draft", fn: () => go && go("chat", { mode: "email", ctx: { uni: u?.name, program: u?.program } }) };
    }
    if (t.uni_id) return { label: "Open", fn: () => go && go("detail", t.uni_id) };
    return null;
  };

  const Row = ({ t }) => {
    const p = parseDue(t.due);
    const uni = universities.find((u) => u.id === t.uni_id || u.name === t.uni);
    const isSchol = effectiveTags(t).includes("Scholarship");
    const act = actionFor(t);
    const soon = p.isOverdue || p.isToday || bucketOf(t) === "week";
    return (
      <div className={`trow ${t.done ? "done" : ""}`} data-testid={`task-row-${t.id}`}>
        <div className="chk" onClick={() => toggle(t)} data-testid={`task-checkbox-${t.id}`}>
          <svg viewBox="0 0 24 24" fill="none"><path d="m5 12 5 5L20 6" /></svg>
        </div>
        <div className="tt">
          <b>{cleanTaskText(t.title)}</b>
          <div className="src">
            {(uni || isSchol) && (
              <span className="utag"><span className="d" style={{ background: isSchol && !uni ? "var(--ok)" : "var(--ink)" }} />{uni ? (uni.short || uni.name) : "Scholarship"}</span>
            )}
            {t.src && t.src !== "Manual" ? <span>{t.src}</span> : null}
          </div>
        </div>
        {p.label && <span className={`tdue vmono ${soon ? "soon" : ""}`} data-testid={`task-due-${t.id}`}>{p.label}</span>}
        {act && <span className="tact" onClick={act.fn} data-testid={`task-action-${t.id}`}>{act.label}</span>}
        <span
          className="tact tact-vidhya"
          title="Work with Vidhya"
          onClick={() => {
            const subs = Array.isArray(t.subtasks) ? t.subtasks : [];
            const subLines = subs.map((s) => `${s.done ? "✓" : "•"} ${cleanTaskText(s.title)}`);
            const msg = `Help me work through my task: "${cleanTaskText(t.title)}".`
              + (subLines.length ? `\n\nHere are the subtasks:\n${subLines.join("\n")}` : "")
              + `\n\nWhat should I tackle first, and how should I approach it?`;
            go && go("chat", {
              mode: "menu",
              ctx: { task_id: t.id, task_title: t.title, uni: uni?.name, program: uni?.program },
              options: { inputPreset: msg },
            });
          }}
          data-testid={`task-vidhya-${t.id}`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}><path d="M12 3v3M12 18v3M5 12H2M22 12h-3M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2" /></svg>
          Work with Vidhya
        </span>
        <span className="tdel" onClick={() => remove(t)} title="Delete" data-testid={`task-delete-${t.id}`}>
          <I.Trash size={14} />
        </span>
      </div>
    );
  };

  const totalOpen = open.length;

  return (
    <div className="vwrap vtasks" data-testid="tasks-view">
      <div className="vphead">
        <div>
          <div className="ptitle">Task command center</div>
          <div className="psub">Everything across all {universities.length} universities, in one place — what to do, and when.</div>
        </div>
        <button className="vp-cta" onClick={() => go && go("chat", { mode: "todo" })} data-testid="tasks-ask-vidhya">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v3M12 18v3M5 12H2M22 12h-3M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2" /></svg>
          Ask Vidhya what to prioritize
        </button>
      </div>

      <AddBar universities={universities} onAdd={(p) => addTask({ ...p, src: "Manual", priority: "med" })} />

      <div className="tcfilters">
        <div className="searchbar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.4-3.4" /></svg>
          <input placeholder="Search tasks…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tasks-search" />
        </div>
        <select className="tcsel" value={uniFilter} onChange={(e) => setUniFilter(e.target.value)} data-testid="tasks-filter-uni">
          <option value="all">All universities</option>
          {universities.map((u) => <option key={u.id} value={u.id}>{u.short || u.name}</option>)}
        </select>
        <select className="tcsel" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="tasks-filter-tag">
          <option value="all">All types</option>
          {TAGS.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
        </select>
      </div>

      {totalOpen === 0 && (
        <div className="vempty" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-card)", padding: 30 }} data-testid="tasks-empty">
          {search || uniFilter !== "all" || tagFilter !== "all" ? "No tasks match your filters." : "You're all caught up. Nice."}
        </div>
      )}

      {GROUPS.map((grp) => grouped[grp.key].length > 0 && (
        <div className="tgroup" key={grp.key} data-testid={`task-group-${grp.key}`}>
          <div className={`tgh ${grp.over ? "over" : ""}`}><b>{grp.label}</b><span className="tc">{grouped[grp.key].length}</span></div>
          {grouped[grp.key].map((t) => <Row key={t.id} t={t} />)}
        </div>
      ))}

      {completed.length > 0 && (
        <div className="tgroup" data-testid="task-group-completed">
          <div className="tgh" style={{ cursor: "pointer" }} onClick={() => setShowDone((s) => !s)} data-testid="tasks-toggle-completed">
            <b style={{ color: "var(--slate)" }}>Completed</b><span className="tc">{completed.length}</span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--indigo)", fontWeight: 700 }}>{showDone ? "Hide" : "Show"}</span>
          </div>
          {showDone && completed.map((t) => <Row key={t.id} t={t} />)}
        </div>
      )}
    </div>
  );
}

function AddBar({ universities, onAdd }) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [tag, setTag] = useState("");
  const [uniId, setUniId] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    const clean = title.trim();
    if (!clean || busy) return;
    setBusy(true);
    const uni = universities.find((u) => u.id === uniId);
    try {
      await onAdd({ title: clean, due: due || "", tags: tag ? [tag] : [], uni: uni ? uni.name : "All applications", uni_id: uni ? uni.id : null });
      setTitle(""); setDue(""); setUniId(""); setTag("");
      inputRef.current && inputRef.current.focus();
    } finally { setBusy(false); }
  };

  return (
    <div className="tcadd" data-testid="tc-add-task">
      <input ref={inputRef} className="tcadd-input" placeholder="Add a task…" value={title}
        onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} data-testid="tc-add-task-input" />
      <input className="tcadd-date" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="tc-add-due" />
      <select className="tcsel" value={uniId} onChange={(e) => setUniId(e.target.value)} data-testid="tc-add-uni">
        <option value="">No university</option>
        {universities.map((u) => <option key={u.id} value={u.id}>{u.short || u.name}</option>)}
      </select>
      <select className="tcsel" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="tc-add-tag">
        <option value="">No type</option>
        {TAGS.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
      </select>
      <button className="tcadd-btn" onClick={submit} disabled={!title.trim() || busy} data-testid="tc-add-task-submit">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>Add
      </button>
    </div>
  );
}
